import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { sesionCajaService, SesionCaja } from '../../services/sesionCajaService';
import { cajaService } from '../../services/cajaService';
import { useAuthStore } from '../../store/authStore';
import AbrirSesionForm from '../../components/SesionesCaja/AbrirSesionForm';
import CerrarSesionForm from '../../components/SesionesCaja/CerrarSesionForm';

const SesionesCajaList = () => {
  const [page, setPage] = useState(1);
  const [estadoFilter, setEstadoFilter] = useState<string>('');
  const [cajaFilter, setCajaFilter] = useState<number | undefined>(undefined);
  const [showAbrirForm, setShowAbrirForm] = useState(false);
  const [showCerrarForm, setShowCerrarForm] = useState(false);
  const [selectedSesion, setSelectedSesion] = useState<SesionCaja | null>(null);
  const queryClient = useQueryClient();
  const { user } = useAuthStore();

  const { data: cajasData } = useQuery(
    'cajas',
    () => cajaService.getAll(1, 100)
  );

  const { data, isLoading } = useQuery(
    ['sesiones-caja', page, estadoFilter, cajaFilter],
    () => sesionCajaService.getAll(page, 20, cajaFilter, estadoFilter || undefined)
  );

  const handleAbrir = () => {
    setShowAbrirForm(true);
  };

  const handleCerrar = (sesion: SesionCaja) => {
    setSelectedSesion(sesion);
    setShowCerrarForm(true);
  };

  const handleCloseForms = () => {
    setShowAbrirForm(false);
    setShowCerrarForm(false);
    setSelectedSesion(null);
  };

  if (isLoading) {
    return <div className="text-center py-8">Cargando...</div>;
  }

  const estados = [
    { value: '', label: 'Todos' },
    { value: 'abierta', label: 'Abierta' },
    { value: 'cerrada', label: 'Cerrada' },
    { value: 'cancelada', label: 'Cancelada' }
  ];

  const getEstadoColor = (estado: string) => {
    const colors: { [key: string]: string } = {
      abierta: 'bg-green-100 text-green-800',
      cerrada: 'bg-gray-100 text-gray-800',
      cancelada: 'bg-red-100 text-red-800'
    };
    return colors[estado] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-800">Sesiones de Caja</h1>
        <button
          onClick={handleAbrir}
          className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 transition"
        >
          + Abrir Sesión
        </button>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b space-y-4">
          <div className="flex space-x-4">
            <select
              value={estadoFilter}
              onChange={(e) => {
                setEstadoFilter(e.target.value);
                setPage(1);
              }}
              className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              {estados.map((estado) => (
                <option key={estado.value} value={estado.value}>
                  {estado.label}
                </option>
              ))}
            </select>
            <select
              value={cajaFilter || ''}
              onChange={(e) => {
                setCajaFilter(e.target.value ? Number(e.target.value) : undefined);
                setPage(1);
              }}
              className="px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">Todas las cajas</option>
              {cajasData?.data.cajas.map((caja) => (
                <option key={caja.id} value={caja.id}>
                  {caja.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Caja</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Usuario</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Apertura</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cierre</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Monto Apertura</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Ventas</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Diferencia</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data?.data.sesiones.map((sesion) => {
                const diferencia = sesion.montoEfectivoReal && sesion.montoEfectivoEsperado
                  ? Number(sesion.montoEfectivoReal) - Number(sesion.montoEfectivoEsperado)
                  : 0;
                
                return (
                  <tr key={sesion.id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">#{sesion.id}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {sesion.caja?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {sesion.usuario?.name || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(sesion.fechaApertura).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {sesion.fechaCierre ? new Date(sesion.fechaCierre).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${Number(sesion.montoApertura).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      ${Number(sesion.totalVentas).toFixed(2)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {sesion.fechaCierre && (
                        <span className={diferencia !== 0 ? (diferencia > 0 ? 'text-green-600' : 'text-red-600') : 'text-gray-600'}>
                          {diferencia > 0 ? '+' : ''}${diferencia.toFixed(2)}
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getEstadoColor(sesion.estado)}`}>
                        {sesion.estado.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      {sesion.estado === 'abierta' && (
                        <button
                          onClick={() => handleCerrar(sesion)}
                          className="text-primary-600 hover:text-primary-900"
                        >
                          Cerrar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {data?.data.pagination && (
          <div className="p-4 border-t flex justify-between items-center">
            <div className="text-sm text-gray-700">
              Mostrando {((page - 1) * 20) + 1} a {Math.min(page * 20, data.data.pagination.total)} de {data.data.pagination.total}
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage(p => p + 1)}
                disabled={page >= data.data.pagination.totalPages}
                className="px-3 py-1 border rounded disabled:opacity-50"
              >
                Siguiente
              </button>
            </div>
          </div>
        )}
      </div>

      {showAbrirForm && (
        <AbrirSesionForm onClose={handleCloseForms} />
      )}

      {showCerrarForm && selectedSesion && (
        <CerrarSesionForm sesion={selectedSesion} onClose={handleCloseForms} />
      )}
    </div>
  );
};

export default SesionesCajaList;

